import { defineStore } from 'pinia';
import type { Product, Category, Categories } from '@/models';
import { WebshopService } from '@/services/api';

interface ProductsState {
  // Per-organisation caches
  productsByOrganisation: Map<string, Product[]>;
  categoriesByOrganisation: Map<string, Category[]>;
  // Track fetch times per organisation
  productsLastFetchTime: Map<string, number>;
  categoriesLastFetchTime: Map<string, number>;
  // Loading states
  isLoadingProducts: boolean;
  isLoadingCategories: boolean;
  // Error state
  error: string | null;
  // Cache duration in milliseconds
  cacheDuration: number;
  // Pending requests for deduplication
  pendingRequests: Map<string, Promise<unknown>>;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useProductsStore = defineStore('products', {
  state: (): ProductsState => ({
    productsByOrganisation: new Map(),
    categoriesByOrganisation: new Map(),
    productsLastFetchTime: new Map(),
    categoriesLastFetchTime: new Map(),
    isLoadingProducts: false,
    isLoadingCategories: false,
    error: null,
    cacheDuration: CACHE_DURATION,
    pendingRequests: new Map()
  }),

  getters: {
    /**
     * Check if products cache is valid for a specific organisation
     */
    isProductsCacheValid: (state) => (organisationId: string): boolean => {
      const lastFetchTime = state.productsLastFetchTime.get(organisationId);
      if (!lastFetchTime) {
        return false;
      }
      return Date.now() - lastFetchTime < state.cacheDuration;
    },

    /**
     * Check if categories cache is valid for a specific organisation
     */
    isCategoriesCacheValid: (state) => (organisationId: string): boolean => {
      const lastFetchTime = state.categoriesLastFetchTime.get(organisationId);
      if (!lastFetchTime) {
        return false;
      }
      return Date.now() - lastFetchTime < state.cacheDuration;
    },

    /**
     * Get cached products for an organisation
     */
    getProducts: (state) => (organisationId: string): Product[] =>
      (state.productsByOrganisation.get(organisationId) || []),

    /**
     * Get cached categories for an organisation
     */
    getCategories: (state) => (organisationId: string): Category[] =>
      (state.categoriesByOrganisation.get(organisationId) || []),

    /**
     * Get a specific product by its public identifier
     */
    getProductById: (state) => (organisationId: string, productId: string): Product | null => {
      const products = state.productsByOrganisation.get(organisationId);
      if (!products) {
        return null;
      }
      return products.find((p) => p.publicIdentifier === productId) || null;
    },

    /**
     * Get products indexed by public identifier for quick lookup
     */
    getProductsById: (state) => (organisationId: string): Record<string, Product> => {
      const products = state.productsByOrganisation.get(organisationId);
      if (!products) {
        return {};
      }
      return products.reduce((acc, product) => {
        acc[product.publicIdentifier] = product;
        return acc;
      }, {} as Record<string, Product>);
    }
  },

  actions: {
    /**
     * Fetch all products for an organisation with caching and deduplication
     * @param organisationId - Organisation ID
     * @param forceRefresh - Force refresh ignoring cache
     * @returns Promise with products array
     */
    async fetchProducts(
      organisationId: string,
      forceRefresh = false
    ): Promise<Product[]> {
      // Return cached data if valid and not forcing refresh
      if (!forceRefresh && this.isProductsCacheValid(organisationId)) {
        const cached = this.productsByOrganisation.get(organisationId);
        if (cached && cached.length > 0) {
          return cached;
        }
      }

      // Check if there's already a pending request for this organisation
      const requestKey = `fetchProducts:${organisationId}`;
      const pendingRequest = this.pendingRequests.get(requestKey);
      if (pendingRequest) {
        return pendingRequest as Promise<Product[]>;
      }

      // Create new request
      const requestPromise = (async (): Promise<Product[]> => {
        try {
          this.isLoadingProducts = true;
          this.error = null;

          const webshopService = new WebshopService(organisationId);
          const response = await webshopService.getAllProducts();

          // Handle response - getAllProducts returns AxiosResponse<Product[]>
          // Extract data from AxiosResponse or handle direct array (for testing)
          const products: Product[] = Array.isArray(response)
            ? response
            : (response as { data?: Product[] })?.data || [];

          this.productsByOrganisation.set(organisationId, products);
          this.productsLastFetchTime.set(organisationId, Date.now());

          return products;
        } catch (error) {
          this.error = error instanceof Error ? error.message : 'Failed to fetch products';
          throw error;
        } finally {
          this.isLoadingProducts = false;
          this.pendingRequests.delete(requestKey);
        }
      })();

      this.pendingRequests.set(requestKey, requestPromise);
      return await requestPromise;
    },

    /**
     * Fetch categories for an organisation with caching and deduplication
     * @param organisationId - Organisation ID
     * @param forceRefresh - Force refresh ignoring cache
     * @returns Promise with categories array
     */
    async fetchCategories(
      organisationId: string,
      forceRefresh = false
    ): Promise<Category[]> {
      // Return cached data if valid and not forcing refresh
      if (!forceRefresh && this.isCategoriesCacheValid(organisationId)) {
        const cached = this.categoriesByOrganisation.get(organisationId);
        if (cached && cached.length > 0) {
          return cached;
        }
      }

      // Check if there's already a pending request for this organisation
      const requestKey = `fetchCategories:${organisationId}`;
      const pendingRequest = this.pendingRequests.get(requestKey);
      if (pendingRequest) {
        return pendingRequest as Promise<Category[]>;
      }

      // Create new request
      const requestPromise = (async (): Promise<Category[]> => {
        try {
          this.isLoadingCategories = true;
          this.error = null;

          const webshopService = new WebshopService(organisationId);
          const response = await webshopService.index() as unknown as Categories;

          const categories: Category[] = response?.productCategories || [];

          this.categoriesByOrganisation.set(organisationId, categories);
          this.categoriesLastFetchTime.set(organisationId, Date.now());

          return categories;
        } catch (error) {
          this.error = error instanceof Error ? error.message : 'Failed to fetch categories';
          throw error;
        } finally {
          this.isLoadingCategories = false;
          this.pendingRequests.delete(requestKey);
        }
      })();

      this.pendingRequests.set(requestKey, requestPromise);
      return await requestPromise;
    },

    /**
     * Invalidate cache for a specific organisation or all organisations
     * @param organisationId - Optional organisation ID. If not provided, clears all caches.
     */
    invalidateCache(organisationId?: string): void {
      if (organisationId) {
        // Clear direct organisation cache
        this.productsByOrganisation.delete(organisationId);
        this.categoriesByOrganisation.delete(organisationId);
        this.productsLastFetchTime.delete(organisationId);
        this.categoriesLastFetchTime.delete(organisationId);

        // Also clear category-specific caches for this organisation
        // These use composite keys like "organisationId:categorySlug"
        const keysToDelete: string[] = [];
        this.productsByOrganisation.forEach((_, key) => {
          if (key.startsWith(`${organisationId}:`)) {
            keysToDelete.push(key);
          }
        });
        keysToDelete.forEach((key) => {
          this.productsByOrganisation.delete(key);
          this.productsLastFetchTime.delete(key);
        });
      } else {
        this.productsByOrganisation.clear();
        this.categoriesByOrganisation.clear();
        this.productsLastFetchTime.clear();
        this.categoriesLastFetchTime.clear();
      }
      this.error = null;
    },

    /**
     * Invalidate only products cache for a specific organisation
     * @param organisationId - Organisation ID
     */
    invalidateProductsCache(organisationId: string): void {
      this.productsByOrganisation.delete(organisationId);
      this.productsLastFetchTime.delete(organisationId);
    },

    /**
     * Invalidate only categories cache for a specific organisation
     * @param organisationId - Organisation ID
     */
    invalidateCategoriesCache(organisationId: string): void {
      this.categoriesByOrganisation.delete(organisationId);
      this.categoriesLastFetchTime.delete(organisationId);
    },

    /**
     * Clear all pending requests
     */
    clearPendingRequests(): void {
      this.pendingRequests.clear();
    },

    /**
     * Set cache duration
     * @param duration - Duration in milliseconds
     */
    setCacheDuration(duration: number): void {
      this.cacheDuration = duration;
    },

    /**
     * Fetch products by category for an organisation with caching and deduplication
     * @param organisationId - Organisation ID
     * @param categorySlug - Category slug
     * @param forceRefresh - Force refresh ignoring cache
     * @returns Promise with products array for the category
     */
    async fetchProductsByCategory(
      organisationId: string,
      categorySlug: string,
      forceRefresh = false
    ): Promise<Product[]> {
      const cacheKey = `${organisationId}:${categorySlug}`;

      // Return cached data if valid and not forcing refresh
      if (!forceRefresh && this.isProductsByCategoryCacheValid(organisationId, categorySlug)) {
        const cached = this.productsByOrganisation.get(cacheKey);
        if (cached && cached.length > 0) {
          return cached;
        }
      }

      // Check if there's already a pending request for this organisation/category
      const requestKey = `fetchProductsByCategory:${cacheKey}`;
      const pendingRequest = this.pendingRequests.get(requestKey);
      if (pendingRequest) {
        return pendingRequest as Promise<Product[]>;
      }

      // Create new request
      const requestPromise = (async (): Promise<Product[]> => {
        try {
          this.isLoadingProducts = true;
          this.error = null;

          const webshopService = new WebshopService(organisationId);
          const response = await webshopService.getProducts(categorySlug);

          // Handle response - getProducts returns Product[]
          const products: Product[] = Array.isArray(response) ? response : [];

          this.productsByOrganisation.set(cacheKey, products);
          this.productsLastFetchTime.set(cacheKey, Date.now());

          return products;
        } catch (error) {
          this.error = error instanceof Error ? error.message : 'Failed to fetch products';
          throw error;
        } finally {
          this.isLoadingProducts = false;
          this.pendingRequests.delete(requestKey);
        }
      })();

      this.pendingRequests.set(requestKey, requestPromise);
      return await requestPromise;
    },

    /**
     * Get cached products by category
     * @param organisationId - Organisation ID
     * @param categorySlug - Category slug
     * @returns Cached products or empty array
     */
    getProductsByCategory(organisationId: string, categorySlug: string): Product[] {
      const cacheKey = `${organisationId}:${categorySlug}`;
      return this.productsByOrganisation.get(cacheKey) || [];
    },

    /**
     * Check if products by category cache is valid
     * @param organisationId - Organisation ID
     * @param categorySlug - Category slug
     * @returns Boolean indicating cache validity
     */
    isProductsByCategoryCacheValid(organisationId: string, categorySlug: string): boolean {
      const cacheKey = `${organisationId}:${categorySlug}`;
      const lastFetchTime = this.productsLastFetchTime.get(cacheKey);
      if (!lastFetchTime) {
        return false;
      }
      return Date.now() - lastFetchTime < this.cacheDuration;
    }
  }
});
